import React, { useState, useEffect, useRef } from "react";
import { X, Check, ArrowRight, ArrowLeft, ShoppingBag, Wallet, CreditCard, MapPin, Pencil, KeyRound, ChevronDown, Search } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import StructuredAddressForm from "./StructuredAddressForm";
import MapAddressPicker from "./MapAddressPicker";
import { AL_LOCATIONS, findQarkByLabel, findBashkiByLabel, findBashkiAnywhere } from "../lib/al_locations";
import { DIAL_CODES, DEFAULT_DIAL } from "../lib/dialCodes";

/*
 * Two payment methods:
 *   "cod"  → POST /api/orders (existing path, instant order creation)
 *   "card" → POST /api/payments/create-checkout-session → redirect to Stripe → on return,
 *             poll /api/payments/status/{session_id} until paid → backend creates order →
 *             we show the same success screen.
 *
 * Two address-capture modes (segmented control, persists in localStorage):
 *   "map"    → opens MapAddressPicker overlay; result auto-fills the structured fields silently
 *   "manual" → user types into StructuredAddressForm
 */
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8; // ~16s total

const EMPTY_ADDR = {
    country: "Albania",
    state: "",
    municipality: "",
    locality: "",
    postal_code: "",
    address: "",
    area: "",            // area / neighborhood / complex (manual)
    apartment_tower: "", // apartment / tower / floor (manual)
    apartment: "",       // house / apartment number
    address_lat: null,
    address_lng: null,
    place_id: null,
};

const CheckoutModal = ({ open, onClose }) => {
    const { t, formatPrice } = useI18n();
    const { items, subtotal, clear } = useCart();
    const { user } = useAuth();

    const [name, setName] = useState("");
    const [addr, setAddr] = useState(EMPTY_ADDR);
    const [addressMode, setAddressMode] = useState(
        () => localStorage.getItem("bb_addr_mode") || "manual"
    );
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [notes, setNotes] = useState("");
    const [waDial, setWaDial] = useState(DEFAULT_DIAL); // WhatsApp country code (default AL +355)
    const [waDialOpen, setWaDialOpen] = useState(false);
    const [waDialSearch, setWaDialSearch] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("cod"); // "cod" | "card"
    const [shipping, setShipping] = useState({ shipping_all: 0, total_weight_grams: 0, tier_label: "0 kg", is_tirana_city: false, free_shipping: false });
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false); // true during Stripe-return poll
    const [order, setOrder] = useState(null);
    const polledRef = useRef(false);

    // OTP-confirm step state (Q2b=a — appears AFTER Place-order is clicked)
    const [otpStep, setOtpStep] = useState(false);     // true → showing OTP entry, false → showing form
    const [otpCode, setOtpCode] = useState("");
    const [otpSending, setOtpSending] = useState(false);
    const [otpDevHint, setOtpDevHint] = useState(null); // only present in preview/dev env

    // "Save this address" checkbox state (only enabled in map mode after a pin pick)
    const [saveThisAddress, setSaveThisAddress] = useState(false);
    const [saveAddressLabel, setSaveAddressLabel] = useState("");

    // Country guard (Q1=a): block submit instantly when country is not Albania
    const ALBANIA_VARIANTS = ["albania", "albanien", "shqiperia", "shqipëria", "shqiperi", "al", "alb"];
    const isCountryAlbania = !addr.country
        || ALBANIA_VARIANTS.includes(addr.country.trim().toLowerCase());

    const isAddressValid = !!(
        addr.state &&
        addr.municipality
    ) && isCountryAlbania;

    // For backend: a one-line "address" goes in the legacy `address` field (used in SMS + receipts).
    // We send it alongside the structured fields.
    const combinedAddress = [
        addr.apartment,
        addr.apartment_tower,
        addr.area,
        addr.address,
        addr.locality,
        addr.municipality,
        addr.postal_code,
        addr.state,
        addr.country || "Albania",
    ].filter(Boolean).join(", ");

    useEffect(() => {
        if (!open) {
            setOrder(null);
            setPaying(false);
            polledRef.current = false;
            setOtpStep(false);
            setOtpCode("");
            setOtpDevHint(null);
            setSaveThisAddress(false);
            setSaveAddressLabel("");
        }
    }, [open]);

    useEffect(() => {
        if (open && user?.name && !name) setName(user.name);
    }, [open, user, name]);

    // Fetch the user's saved address book (max 3) for the dropdown
    useEffect(() => {
        if (!open || !user) return;
        api.get("/user/saved-addresses")
            .then((r) => setSavedAddresses(r.data.saved_addresses || []))
            .catch(() => setSavedAddresses([]));
    }, [open, user]);

    const pickSavedAddress = (a) => {
        if (!a) return;
        setAddr({
            country: a.country || "Albania",
            state: a.state || "",
            municipality: a.municipality || "",
            locality: a.locality || "",
            postal_code: a.postal_code || "",
            address: a.address_only || a.address || "",
            address_lat: a.address_lat ?? a.lat ?? null,
            address_lng: a.address_lng ?? a.lng ?? null,
            place_id: a.place_id ?? null,
        });
    };

    // Live shipping preview — recompute whenever cart OR municipality changes
    useEffect(() => {
        if (!open || !items.length) {
            setShipping({ shipping_all: 0, total_weight_grams: 0, tier_label: "0 kg", is_tirana_city: false, free_shipping: false });
            return;
        }
        let cancelled = false;
        api.post("/shipping/preview", {
            items: items.map((i) => ({ id: i.id, qty: i.qty, weight_grams: i.weight_grams || null, weight: i.weight || null, price_all: i.price_all || null })),
            municipality: addr.municipality || null,
            subtotal_all: subtotal,
        }).then((r) => {
            if (!cancelled) setShipping(r.data);
        }).catch(() => {
            if (!cancelled) setShipping({ shipping_all: 0, total_weight_grams: 0, tier_label: "?", is_tirana_city: false, free_shipping: false });
        });
        return () => { cancelled = true; };
    }, [open, items, addr.municipality, subtotal]);

    // ─── Handle Stripe redirect-back ────────────────────────────
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const stripeSession = params.get("stripe_session");
        const cancelled = params.get("stripe_cancel");

        if (cancelled) {
            toast.error(t("checkout_payment_cancelled"));
            const url = new URL(window.location.href);
            url.searchParams.delete("stripe_cancel");
            window.history.replaceState({}, "", url.toString());
            return;
        }

        if (!stripeSession || polledRef.current) return;
        polledRef.current = true;
        setPaying(true);

        const cleanUrl = () => {
            const url = new URL(window.location.href);
            url.searchParams.delete("stripe_session");
            window.history.replaceState({}, "", url.toString());
        };

        const poll = async (attempt) => {
            try {
                const r = await api.get(`/payments/status/${stripeSession}`);
                const data = r.data;
                if (data.payment_status === "paid" && data.order) {
                    setOrder(data.order);
                    setPaying(false);
                    clear();
                    cleanUrl();
                    return;
                }
                if (data.status === "expired") {
                    toast.error(t("checkout_payment_expired"));
                    setPaying(false);
                    cleanUrl();
                    return;
                }
                if (attempt + 1 < POLL_MAX_ATTEMPTS) {
                    setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
                } else {
                    toast.error(t("checkout_payment_timeout"));
                    setPaying(false);
                    cleanUrl();
                }
            } catch (err) {
                toast.error(err.response?.data?.detail || t("toast_order_error"));
                setPaying(false);
                cleanUrl();
            }
        };
        poll(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!open && !paying) return null;

    // Build the JSON payload shared by COD + Card flows
    const orderPayload = () => ({
        items: items.map((i) => ({
            id: i.id, name: i.name, price_all: i.price_all, qty: i.qty,
            image: i.image, weight: i.weight, weight_grams: i.weight_grams ?? null,
        })),
        address: combinedAddress,
        address_lat: addr.address_lat,
        address_lng: addr.address_lng,
        place_id: addr.place_id,
        country: addr.country || null,
        state: addr.state || null,
        municipality: addr.municipality || null,
        locality: addr.locality || null,
        postal_code: addr.postal_code || null,
        address_mode: addressMode,
        name: name || null,
        notes: notes.trim() ? `${waDial.dial} ${notes.trim()}` : null,
    });

    const placeCodOrder = async (otpOverride) => {
        const r = await api.post("/orders", { ...orderPayload(), otp: otpOverride || otpCode });
        setOrder(r.data.order);
        clear();
        // If the user ticked "Save this address", best-effort persist it now.
        // Failure here doesn't block the order — we just toast a hint.
        if (saveThisAddress && addr.address_lat != null) {
            try {
                await api.post("/user/saved-addresses", {
                    label: saveAddressLabel.trim() || "Saved on " + new Date().toLocaleDateString(),
                    address: combinedAddress,
                    country: addr.country,
                    state: addr.state,
                    municipality: addr.municipality,
                    locality: addr.locality,
                    postal_code: addr.postal_code,
                    address_lat: addr.address_lat,
                    address_lng: addr.address_lng,
                    place_id: addr.place_id,
                });
                toast.success("Address saved for future orders.");
            } catch (err) {
                // Most likely they already had 3 saved — show the server's message
                toast(err.response?.data?.detail || "Couldn't save address (limit reached?). Order placed successfully.");
            }
        }
    };

    const startCardPayment = async () => {
        const r = await api.post("/payments/create-checkout-session", {
            ...orderPayload(),
            origin_url: window.location.origin,
        });
        if (r.data.url) {
            window.location.href = r.data.url;
        } else {
            throw new Error("No checkout URL");
        }
    };

    // Step 1: user fills form + hits "Place order" → send OTP, switch to OTP step
    const submit = async (e) => {
        e.preventDefault();
        if (!items.length) return;
        if (!isCountryAlbania) {
            toast.error("Sorry, we cannot deliver to the selected address. We currently ship only inside Albania.");
            return;
        }
        if (!isAddressValid) {
            toast.error("Please complete the address (Qark, Bashkia and Road).");
            return;
        }
        setOtpSending(true);
        try {
            const r = await api.post("/orders/send-otp", {});
            setOtpDevHint(r.data?.dev_otp || null);
            setOtpStep(true);
            setOtpCode("");
            toast.success(`Code sent to ${r.data?.phone || user?.phone}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send OTP. Try again.");
        } finally {
            setOtpSending(false);
        }
    };

    // Step 2: user typed the OTP → verify + create order
    const confirmWithOtp = async (e) => {
        e.preventDefault();
        if (!otpCode || otpCode.length < 4) {
            toast.error("Enter the 6-digit code we sent you.");
            return;
        }
        setLoading(true);
        try {
            if (paymentMethod === "card") {
                await startCardPayment();
            } else {
                await placeCodOrder(otpCode);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast_order_error"));
        } finally {
            setLoading(false);
        }
    };

    const resendOtp = async () => {
        setOtpSending(true);
        try {
            const r = await api.post("/orders/send-otp", {});
            setOtpDevHint(r.data?.dev_otp || null);
            setOtpCode("");
            toast.success("New code sent.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not resend.");
        } finally {
            setOtpSending(false);
        }
    };

    const onMapConfirm = (picked) => {
        if (!picked) {
            setMapPickerOpen(false);
            return;
        }
        // Snap Google's reverse-geocode values to our canonical qark/bashki names
        // (e.g. "Tirana" → "Tiranë") so the State/City dropdowns can show them as selected.
        let snappedState = picked.state || "";
        let snappedMuni = picked.municipality || "";
        const qarkMatch = findQarkByLabel(snappedState);
        if (qarkMatch) {
            snappedState = qarkMatch.label;
            const bashkiMatch = findBashkiByLabel(qarkMatch.id, snappedMuni);
            if (bashkiMatch) snappedMuni = bashkiMatch.label;
        } else if (snappedMuni) {
            // The map didn't give a usable state. Try to infer it from the city alone.
            const found = findBashkiAnywhere(snappedMuni);
            if (found) {
                snappedState = found.qark.label;
                snappedMuni = found.bashki.label;
            }
        }
        // Bug 2 fix: a fresh pick must FULLY replace location data — do NOT inherit
        // the previous pick's locality/postal_code, otherwise old values bleed through
        // when the new region doesn't have them. We rebuild the addr from scratch,
        // keeping only the apartment text the user typed manually.
        setAddr((prev) => ({
            ...EMPTY_ADDR,
            country: picked.country || "Albania",
            state: snappedState || "",
            municipality: snappedMuni || "",
            locality: picked.locality || "",
            postal_code: picked.postal_code || "",
            address: picked.street || picked.formatted_address || "",
            address_lat: picked.lat,
            address_lng: picked.lng,
            place_id: picked.place_id || null,
            // Preserve user-typed apartment/area/tower from previous interaction with the same flow.
            apartment: prev.apartment || "",
            apartment_tower: prev.apartment_tower || "",
            area: prev.area || "",
        }));
        setMapPickerOpen(false);
        toast.success("Location captured. Review the summary below.");
    };

    // Bug 3 fix: switching modes wipes the address state so the user starts fresh.
    // Country defaults back to Albania.
    const pickMode = (m) => {
        if (m !== addressMode) {
            setAddr({ ...EMPTY_ADDR });
            setSaveThisAddress(false);
            setSaveAddressLabel("");
        }
        setAddressMode(m);
        localStorage.setItem("bb_addr_mode", m);
    };

    return (
        <div
            data-testid="checkout-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && !order && !paying && onClose()}
        >
            <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-7 md:p-9 shadow-2xl relative">
                {!order && !paying && (
                    <button
                        data-testid="checkout-close-btn"
                        onClick={onClose}
                        className="absolute top-5 right-5 h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center z-10"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}

                {paying && !order && (
                    <div data-testid="checkout-paying" className="text-center py-10">
                        <div className="h-16 w-16 mx-auto rounded-full border-4 border-[#FF5722] border-t-transparent animate-spin" />
                        <h2 className="font-display text-2xl font-medium tracking-tight mt-6">
                            {t("checkout_verifying_payment")}
                        </h2>
                        <p className="text-neutral-500 mt-2 text-sm">
                            {t("checkout_verifying_payment_hint")}
                        </p>
                    </div>
                )}

                {order && (
                    <div data-testid="order-success" className="text-center py-4">
                        <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <Check className="h-8 w-8" strokeWidth={2.5} />
                        </div>
                        <h2 className="font-display text-3xl font-medium tracking-tight mt-6">
                            {t("checkout_success_title")}
                        </h2>
                        <p className="text-neutral-600 mt-3 text-[15px]">
                            {t("checkout_success_subtitle")}
                        </p>
                        <div
                            data-testid="order-id"
                            className="mt-3 inline-block px-4 py-2 rounded-full bg-neutral-100 font-mono text-sm font-semibold"
                        >
                            #{order.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="mt-6 text-left bg-neutral-50 rounded-2xl p-5">
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-neutral-600">{t("checkout_subtotal") || t("cart_subtotal")}</span>
                                    <span className="font-semibold">{formatPrice(order.subtotal_all).primary}</span>
                                </div>
                                {order.shipping_all > 0 && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-neutral-600">{t("checkout_shipping")}</span>
                                        <span className="font-semibold">{formatPrice(order.shipping_all).primary}</span>
                                    </div>
                                )}
                                <div className="border-t border-neutral-200 pt-2 mt-2 flex items-center justify-between">
                                    <span className="text-neutral-700 font-semibold">{t("checkout_total")}</span>
                                    <span className="font-display text-xl font-semibold">
                                        {formatPrice(order.total_all ?? order.subtotal_all).primary}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-neutral-500 mt-3">
                                {order.items.length} {t("filter_all").toLowerCase()} · {order.address}
                            </div>
                            {order.payment_method && (
                                <div className="text-xs text-neutral-500 mt-2 inline-flex items-center gap-1.5">
                                    {order.payment_method === "card"
                                        ? <><CreditCard className="h-3.5 w-3.5" /> {t("payment_method_card_paid")}</>
                                        : <><Wallet className="h-3.5 w-3.5" /> {t("payment_method_cod")}</>}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="order-continue-btn"
                            className="mt-7 w-full h-14 rounded-xl bg-[#1a1a1a] hover:bg-[#FF5722] text-white font-semibold transition-all"
                        >
                            {t("checkout_continue")}
                        </button>
                    </div>
                )}

                {!order && !paying && otpStep && (
                    <form onSubmit={confirmWithOtp} data-testid="checkout-otp-step">
                        <button
                            type="button"
                            onClick={() => { setOtpStep(false); setOtpCode(""); setOtpDevHint(null); }}
                            data-testid="checkout-otp-back-btn"
                            className="text-sm text-neutral-600 hover:text-[#FF5722] inline-flex items-center gap-1 mb-3"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back
                        </button>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <KeyRound className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            Confirm your order
                        </h2>
                        <p className="text-neutral-600 mt-2 text-[15px]">
                            We&apos;ve sent a 6-digit code to{" "}
                            <span className="font-semibold text-neutral-900">{user?.phone}</span>
                            . Enter it to place your order.
                        </p>

                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-6 mb-2">
                            Verification code
                        </label>
                        <input
                            data-testid="checkout-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            autoFocus
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            className="w-full h-14 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none transition-all text-center text-2xl tracking-[0.4em] font-semibold"
                        />
                        {otpDevHint && (
                            <p
                                data-testid="checkout-otp-devhint"
                                className="mt-2 text-[11px] text-amber-600 font-mono"
                            >
                                Dev mode: code is {otpDevHint}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={loading || otpCode.length < 6}
                            data-testid="checkout-otp-confirm-btn"
                            className="mt-5 w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? t("checkout_placing") : <>Confirm &amp; place order <ArrowRight className="h-5 w-5" /></>}
                        </button>
                        <button
                            type="button"
                            onClick={resendOtp}
                            disabled={otpSending}
                            data-testid="checkout-otp-resend-btn"
                            className="mt-3 w-full h-11 text-sm text-neutral-600 hover:text-[#FF5722] font-semibold disabled:opacity-50"
                        >
                            {otpSending ? "Sending…" : "Resend code"}
                        </button>
                    </form>
                )}

                {!order && !paying && !otpStep && (
                    <form onSubmit={submit}>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <ShoppingBag className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            {t("checkout_title")}
                        </h2>
                        <p className="text-sm text-neutral-500 mt-1">{user?.phone}</p>

                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-6 mb-2">
                            {t("checkout_name_label")}
                        </label>
                        <input
                            data-testid="checkout-name-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full h-12 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none transition-all"
                        />

                        <div className="mt-5 flex items-center justify-between">
                            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                {t("checkout_address_label")} *
                            </label>
                            {/* Segmented control: Map / Manual */}
                            <div
                                role="tablist"
                                data-testid="address-mode-tabs"
                                className="inline-flex p-0.5 bg-neutral-100 rounded-lg text-[11px] font-semibold"
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={addressMode === "map"}
                                    onClick={() => pickMode("map")}
                                    data-testid="address-mode-map"
                                    className={`px-3 h-7 rounded-md inline-flex items-center gap-1 transition-all ${
                                        addressMode === "map"
                                            ? "bg-white shadow-sm text-[#FF5722]"
                                            : "text-neutral-500 hover:text-neutral-700"
                                    }`}
                                >
                                    <MapPin className="h-3 w-3" /> Map
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={addressMode === "manual"}
                                    onClick={() => pickMode("manual")}
                                    data-testid="address-mode-manual"
                                    className={`px-3 h-7 rounded-md inline-flex items-center gap-1 transition-all ${
                                        addressMode === "manual"
                                            ? "bg-white shadow-sm text-[#FF5722]"
                                            : "text-neutral-500 hover:text-neutral-700"
                                    }`}
                                >
                                    <Pencil className="h-3 w-3" /> Manual
                                </button>
                            </div>
                        </div>

                        {savedAddresses.length > 0 && (
                            <div className="mt-3" data-testid="saved-addresses-dropdown-wrap">
                                <label
                                    htmlFor="saved-addresses-dropdown"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-1.5"
                                >
                                    Saved addresses
                                </label>
                                <select
                                    id="saved-addresses-dropdown"
                                    data-testid="saved-addresses-dropdown"
                                    value=""
                                    onChange={(e) => {
                                        const a = savedAddresses.find((x) => x.id === e.target.value);
                                        if (a) {
                                            pickSavedAddress(a);
                                            toast.success(`Using address: ${a.label}`);
                                        }
                                    }}
                                    className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm font-medium text-neutral-700"
                                >
                                    <option value="" disabled>
                                        Choose a saved address…
                                    </option>
                                    {savedAddresses.map((a) => (
                                        <option
                                            key={a.id}
                                            value={a.id}
                                            data-testid={`saved-address-option-${a.id}`}
                                        >
                                            {a.label} — {a.address}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-neutral-400 mt-1.5">
                                    Manage saved addresses from <span className="font-semibold">My Profile → Saved addresses</span>.
                                </p>
                            </div>
                        )}

                        {addressMode === "map" && (
                            <div data-testid="address-mode-map-panel" className="mt-3 rounded-2xl border-2 border-dashed border-neutral-200 p-4 text-center bg-neutral-50">
                                <MapPin className="h-7 w-7 mx-auto text-[#FF5722]" strokeWidth={1.6} />
                                <p className="text-sm font-semibold text-neutral-800 mt-2">
                                    {addr.address_lat != null
                                        ? "Location captured"
                                        : "Drop a pin on the map"}
                                </p>
                                <p className="text-[11px] text-neutral-500 mt-1 px-2">
                                    {addr.address_lat != null
                                        ? `${addr.municipality || "Picked location"} · ${addr.state || ""}`
                                        : "We'll auto-fill Qark, Bashkia and PIN from the map."}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setMapPickerOpen(true)}
                                    data-testid="open-map-picker-btn"
                                    className="mt-3 h-10 px-4 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white text-sm font-semibold inline-flex items-center gap-2 transition-colors"
                                >
                                    <MapPin className="h-4 w-4" />
                                    {addr.address_lat != null ? "Change pin" : "Open map"}
                                </button>
                                {addr.address_lat != null && (
                                    <p
                                        data-testid="checkout-address-confirmed"
                                        className="mt-3 text-[11px] text-emerald-600 inline-flex items-center gap-1"
                                    >
                                        <Check className="h-3 w-3" />
                                        ({Number(addr.address_lat).toFixed(4)}, {Number(addr.address_lng).toFixed(4)})
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Selected-address summary (shown only in Map mode, after a pin has been dropped) */}
                        {addressMode === "map" && addr.address_lat != null && (
                            <div
                                data-testid="selected-address-summary"
                                className="mt-3 rounded-2xl border border-neutral-200 bg-white p-4"
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-2.5">
                                    Selected address
                                </div>
                                <dl className="space-y-1.5 text-sm">
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-neutral-500">Country</dt>
                                        <dd className="font-medium text-neutral-900 text-right" data-testid="summary-country">
                                            {addr.country || "—"}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-neutral-500">State / Qark</dt>
                                        <dd className="font-medium text-neutral-900 text-right" data-testid="summary-state">
                                            {addr.state || "—"}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-neutral-500">City / Bashkia</dt>
                                        <dd className="font-medium text-neutral-900 text-right" data-testid="summary-municipality">
                                            {addr.municipality || "—"}
                                        </dd>
                                    </div>
                                    {addr.locality && (
                                        <div className="flex justify-between gap-3">
                                            <dt className="text-neutral-500">Neighborhood</dt>
                                            <dd className="font-medium text-neutral-900 text-right">
                                                {addr.locality}
                                            </dd>
                                        </div>
                                    )}
                                    {addr.address && (
                                        <div className="flex justify-between gap-3 pt-1 border-t border-neutral-100">
                                            <dt className="text-neutral-500 shrink-0">Street</dt>
                                            <dd className="font-medium text-neutral-700 text-right text-xs leading-snug ml-3">
                                                {addr.address}
                                            </dd>
                                        </div>
                                    )}
                                </dl>

                                <p className="mt-3 mb-1.5 text-[10px] text-neutral-500">
                                    To change the country/state/city, switch to <button type="button" onClick={() => pickMode("manual")} className="text-[#FF5722] font-semibold underline">Manual</button> mode.
                                </p>

                                <div className="hidden">
                                <label
                                    htmlFor="summary-postal"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-3 mb-1"
                                >
                                    Pin code / Postal code
                                </label>
                                <input
                                    id="summary-postal"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={addr.postal_code || ""}
                                    onChange={(e) => setAddr({ ...addr, postal_code: e.target.value.slice(0, 10) })}
                                    placeholder="1001"
                                    data-testid="summary-postal-input"
                                    className="w-full h-10 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />

                                <label
                                    htmlFor="summary-area"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-3 mb-1"
                                >
                                    Area / Neighborhood / Complex
                                </label>
                                <input
                                    id="summary-area"
                                    type="text"
                                    value={addr.area || ""}
                                    onChange={(e) => setAddr({ ...addr, area: e.target.value })}
                                    placeholder="e.g., Kompleksi Delijorgji, Rruga Bajram Curri"
                                    data-testid="summary-area-input"
                                    className="w-full h-10 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />

                                <label
                                    htmlFor="summary-apartment-tower"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-3 mb-1"
                                >
                                    Apartment / Tower / Floor
                                </label>
                                <input
                                    id="summary-apartment-tower"
                                    type="text"
                                    value={addr.apartment_tower || ""}
                                    onChange={(e) => setAddr({ ...addr, apartment_tower: e.target.value })}
                                    placeholder="e.g., Tower B, 5th floor"
                                    data-testid="summary-apartment-tower-input"
                                    className="w-full h-10 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />

                                <label
                                    htmlFor="summary-apartment"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-3 mb-1"
                                >
                                    House / Apartment number
                                </label>
                                <input
                                    id="summary-apartment"
                                    type="text"
                                    value={addr.apartment || ""}
                                    onChange={(e) => setAddr({ ...addr, apartment: e.target.value })}
                                    placeholder="e.g., 12B"
                                    data-testid="summary-apartment-input"
                                    className="w-full h-10 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />
                                </div>

                                <label
                                    className="mt-3 flex items-start gap-2.5 cursor-pointer select-none"
                                    data-testid="save-address-toggle-label"
                                >
                                    <input
                                        type="checkbox"
                                        checked={saveThisAddress}
                                        onChange={(e) => setSaveThisAddress(e.target.checked)}
                                        data-testid="save-address-toggle"
                                        className="mt-1 h-4 w-4 rounded border-2 border-neutral-300 text-[#FF5722] focus:ring-[#FF5722]/30 cursor-pointer"
                                    />
                                    <span className="text-sm text-neutral-700">
                                        Save this address for future orders
                                    </span>
                                </label>
                                {saveThisAddress && (
                                    <input
                                        type="text"
                                        maxLength={40}
                                        value={saveAddressLabel}
                                        onChange={(e) => setSaveAddressLabel(e.target.value)}
                                        placeholder="Nickname (Home, Office…)"
                                        data-testid="save-address-label-input"
                                        className="mt-2 w-full h-10 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                    />
                                )}
                            </div>
                        )}

                        {addressMode === "manual" && (
                            <div className="mt-3">
                                <StructuredAddressForm value={addr} onChange={setAddr} />

                                <div className="hidden">
                                <label
                                    htmlFor="manual-area"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mt-3 mb-1.5"
                                >
                                    Area / Neighborhood / Complex
                                </label>
                                <input
                                    id="manual-area"
                                    type="text"
                                    value={addr.area || ""}
                                    onChange={(e) => setAddr({ ...addr, area: e.target.value })}
                                    placeholder="e.g., Kompleksi Delijorgji, Rruga Bajram Curri"
                                    data-testid="manual-area-input"
                                    className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />

                                <label
                                    htmlFor="manual-apartment-tower"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mt-3 mb-1.5"
                                >
                                    Apartment / Tower / Floor
                                </label>
                                <input
                                    id="manual-apartment-tower"
                                    type="text"
                                    value={addr.apartment_tower || ""}
                                    onChange={(e) => setAddr({ ...addr, apartment_tower: e.target.value })}
                                    placeholder="e.g., Tower B, 5th floor"
                                    data-testid="manual-apartment-tower-input"
                                    className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />

                                <label
                                    htmlFor="manual-apartment"
                                    className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mt-3 mb-1.5"
                                >
                                    House / Apartment number
                                </label>
                                <input
                                    id="manual-apartment"
                                    type="text"
                                    value={addr.apartment || ""}
                                    onChange={(e) => setAddr({ ...addr, apartment: e.target.value })}
                                    placeholder="e.g., 12B"
                                    data-testid="manual-apartment-input"
                                    className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                />
                                </div>
                            </div>
                        )}

                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-4 mb-2">
                            {t("checkout_alt_phone_label")}
                        </label>
                        <div className="flex gap-2 relative">
                            <button
                                type="button"
                                data-testid="whatsapp-dial-toggle"
                                onClick={() => { setWaDialOpen((v) => !v); setWaDialSearch(""); }}
                                className="h-12 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none inline-flex items-center gap-1.5 shrink-0 hover:border-neutral-300 transition-all"
                            >
                                <span className="text-xl leading-none">{waDial.flag}</span>
                                <span className="font-semibold text-sm">{waDial.dial}</span>
                                <ChevronDown className={`h-3.5 w-3.5 text-neutral-500 transition-transform ${waDialOpen ? "rotate-180" : ""}`} />
                            </button>
                            <input
                                data-testid="checkout-alt-phone-input"
                                type="tel"
                                inputMode="tel"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value.replace(/[^\d\s\-]/g, "").slice(0, 15))}
                                placeholder={t("checkout_alt_phone_placeholder")}
                                className="flex-1 min-w-0 h-12 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none transition-all"
                            />
                            {waDialOpen && (
                                <div
                                    data-testid="whatsapp-dial-dropdown"
                                    className="absolute top-14 left-0 z-20 bg-white rounded-2xl shadow-xl border border-neutral-200 w-72 overflow-hidden"
                                >
                                    <div className="sticky top-0 bg-white p-2 border-b border-neutral-100">
                                        <div className="relative">
                                            <Search className="h-3.5 w-3.5 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                            <input
                                                type="text"
                                                autoFocus
                                                value={waDialSearch}
                                                onChange={(e) => setWaDialSearch(e.target.value)}
                                                placeholder="Search country…"
                                                data-testid="whatsapp-dial-search"
                                                className="w-full h-9 pl-9 pr-3 rounded-lg bg-neutral-50 border border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-56 overflow-y-auto">
                                        {DIAL_CODES.filter((c) => {
                                            const q = waDialSearch.trim().toLowerCase();
                                            return !q || c.name.toLowerCase().includes(q) || c.dial.includes(q.replace(/^\+?/, "+")) || c.dial.replace("+", "").startsWith(q);
                                        }).map((c) => (
                                            <button
                                                key={c.iso}
                                                type="button"
                                                data-testid={`whatsapp-dial-${c.iso}`}
                                                onClick={() => { setWaDial(c); setWaDialOpen(false); }}
                                                className={`w-full px-4 py-2.5 flex items-center gap-3 text-left text-sm transition-colors hover:bg-neutral-50 ${
                                                    waDial.iso === c.iso ? "bg-[#FFF3EE] text-[#FF5722] font-semibold" : "text-neutral-800"
                                                }`}
                                            >
                                                <span className="text-xl leading-none">{c.flag}</span>
                                                <span className="flex-1 min-w-0 truncate">{c.name}</span>
                                                <span className="text-xs text-neutral-500 font-mono">{c.dial}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ─── Payment method selector ─── */}
                        <fieldset className="mt-6">
                            <legend className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-2">
                                {t("checkout_payment_method_label")}
                            </legend>
                            <div className="grid grid-cols-2 gap-2.5">
                                <label
                                    data-testid="payment-method-cod"
                                    className={`cursor-pointer rounded-xl border-2 p-3.5 flex flex-col items-start gap-2 transition-all ${
                                        paymentMethod === "cod"
                                            ? "border-[#FF5722] bg-[#FFF3EE]"
                                            : "border-neutral-200 hover:border-neutral-300 bg-neutral-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="cod"
                                        checked={paymentMethod === "cod"}
                                        onChange={() => setPaymentMethod("cod")}
                                        className="sr-only"
                                    />
                                    <Wallet className={`h-5 w-5 ${paymentMethod === "cod" ? "text-[#FF5722]" : "text-neutral-500"}`} />
                                    <div>
                                        <div className="text-sm font-semibold text-neutral-900">
                                            {t("payment_method_cod")}
                                        </div>
                                        <div className="text-[11px] text-neutral-500 mt-0.5">
                                            {t("payment_method_cod_hint")}
                                        </div>
                                    </div>
                                </label>
                                <label
                                    data-testid="payment-method-card"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toast(t("payment_method_card_coming_soon"));
                                    }}
                                    className="cursor-pointer rounded-xl border-2 p-3.5 flex flex-col items-start gap-2 transition-all border-neutral-200 bg-neutral-50/60 opacity-70 hover:opacity-90 relative"
                                >
                                    <span
                                        data-testid="payment-method-card-badge"
                                        className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider bg-neutral-900 text-white px-1.5 py-0.5 rounded"
                                    >
                                        {t("payment_method_coming_soon")}
                                    </span>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="card"
                                        checked={false}
                                        readOnly
                                        className="sr-only"
                                    />
                                    <CreditCard className="h-5 w-5 text-neutral-400" />
                                    <div>
                                        <div className="text-sm font-semibold text-neutral-500">
                                            {t("payment_method_card")}
                                        </div>
                                        <div className="text-[11px] text-neutral-400 mt-0.5">
                                            {t("payment_method_card_hint")}
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </fieldset>

                        <div className="mt-6 p-4 rounded-2xl bg-neutral-50 space-y-2.5">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-neutral-600">{t("checkout_subtotal") || t("cart_subtotal")}</span>
                                <span
                                    data-testid="checkout-subtotal"
                                    className="font-semibold text-neutral-900"
                                >
                                    {formatPrice(subtotal).primary}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-neutral-600 inline-flex items-center gap-1 flex-wrap">
                                    {t("checkout_shipping")}
                                    {shipping.tier_label && shipping.total_weight_grams > 0 && (
                                        <span className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider">
                                            ({shipping.tier_label}{shipping.is_tirana_city ? " · Tirana" : ""})
                                        </span>
                                    )}
                                    {shipping.free_shipping && (
                                        <span
                                            data-testid="free-shipping-badge"
                                            className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                                        >
                                            Free over 2000 L
                                        </span>
                                    )}
                                </span>
                                <span
                                    data-testid="checkout-shipping"
                                    className={`font-semibold ${shipping.free_shipping ? "text-emerald-700" : "text-neutral-900"}`}
                                >
                                    {shipping.free_shipping ? "FREE" : formatPrice(shipping.shipping_all).primary}
                                </span>
                            </div>
                            <div className="border-t border-neutral-200 pt-2.5 flex items-center justify-between">
                                <span className="text-sm text-neutral-600 font-semibold">{t("checkout_total")}</span>
                                <div className="text-right">
                                    <div
                                        className="font-display text-2xl font-semibold"
                                        data-testid="checkout-total"
                                    >
                                        {formatPrice(subtotal + (shipping.shipping_all || 0)).primary}
                                    </div>
                                    <div className="text-xs text-neutral-400">
                                        {formatPrice(subtotal + (shipping.shipping_all || 0)).secondary}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isCountryAlbania && (
                            <div
                                data-testid="checkout-not-albania-banner"
                                className="mt-5 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 flex items-start gap-2.5"
                            >
                                <span className="text-lg leading-none">⚠</span>
                                <div className="text-sm">
                                    <div className="font-semibold">We cannot deliver to this address.</div>
                                    <div className="text-rose-600/90 text-xs mt-0.5">
                                        myBazaarCart ships only inside Albania. Change the country field above to continue.
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={otpSending || !isAddressValid}
                            data-testid="checkout-place-btn"
                            className="mt-5 w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {otpSending
                                ? "Sending code…"
                                : <>{t("checkout_place")} <ArrowRight className="h-5 w-5" /></>}
                        </button>
                    </form>
                )}
            </div>

            {/* Fullscreen map picker overlay (sits above the modal, z-200) */}
            <MapAddressPicker
                open={mapPickerOpen}
                onClose={() => setMapPickerOpen(false)}
                onConfirm={onMapConfirm}
                initial={{ lat: addr.address_lat, lng: addr.address_lng }}
            />
        </div>
    );
};

export default CheckoutModal;
